import * as z from "zod";
import { type Caller, type CursorPage, cursorPagination, decimal, type Result } from "../../index";

// The orders side of the funnel of a campaign (ADR-287 §2, ADR-290), for the projection of crm: ids and facts, and no
// campaign, which crm attributes from the invitations of booking.

/**
 * Held only by the system Actor of crm, which reads the domains for its projection: no role gives it. One for the whole
 * port, as `bc_sync.reconcile` for the reconciliation, whichever domain serves the part.
 */
export const NEXXT_SOURCE_READ = "nexxt_source.read";

/**
 * The events that wake the projection for an order, and the field of its id (ADR-290 §9): its birth, and every change of
 * its status. What changes without a status (amounts, shipments, invoices) the nightly realignment reads (ADR-292).
 */
export const ORDER_CHANGED_EVENTS = [
  { type: "sales.salesOrderConfirmed.v1", id: "soId" },
  { type: "sales.salesOrderStatusChanged.v1", id: "soId" },
] as const;

/**
 * Where an order stands in the funnel, after "approved on Joor" (the funnel stops there until the orders not yet
 * approved reach Nexxt, T-85):
 * - `approved`: born from the order approved on Joor, its credit decided or not;
 * - `allocated`: stock committed to it;
 * - `locked`: frozen for Business Central, waiting for its payment, its documents or its lots;
 * - `released`: with the warehouse to ship;
 * - `completed`: shipped and invoiced;
 * - `cancelled`.
 */
export const orderFunnelStages = ["approved", "allocated", "locked", "released", "completed", "cancelled"] as const;
export type OrderFunnelStage = (typeof orderFunnelStages)[number];

/** The cursor of the facts: the id of the last order of the page. */
export const orderFunnelCursor = z.tuple([z.uuid()]);

/**
 * The facts of the orders, by id: every order for a full realignment, page after page; the orders of an event
 * (`orderIds`) to read them again; or those of a season, a customer or a brand.
 */
export const listOrderFunnelFactsInput = z.object({
  orderIds: z.array(z.uuid()).min(1).max(100).optional(),
  seasonId: z.uuid().optional(),
  customerOrgId: z.uuid().optional(),
  brandOrgId: z.uuid().optional(),
  ...cursorPagination({ defaultLimit: 500, maxLimit: 1000 }),
});
export type ListOrderFunnelFactsInput = z.input<typeof listOrderFunnelFactsInput>;

/**
 * An order of a customer, in the currency of the order. Amounts to the cent, after the discounts and without the
 * cancelled lines; `invoicedAmount` is what Business Central said it invoiced (I-06), null before it says.
 */
export const orderFunnelFactDto = z
  .object({
    orderId: z.uuid(),
    /** The number users know the order by. */
    orderNumber: z.string(),
    customerOrgId: z.uuid(),
    brandOrgId: z.uuid().nullable(),
    seasonId: z.uuid().nullable(),
    stage: z.enum(orderFunnelStages),
    currency: z.string(),
    /** Towards EUR; null without the rate of Finance for the season. */
    exchangeRate: decimal.nullable(),
    orderedUnits: z.number().int(),
    shippedUnits: z.number().int(),
    orderedAmount: decimal,
    invoicedAmount: decimal.nullable(),
    orderedAt: z.iso.datetime(),
    changedAt: z.iso.datetime(),
  })
  .meta({ id: "OrderFunnelFact" });
export type OrderFunnelFact = z.output<typeof orderFunnelFactDto>;

/** What the domain of the orders serves to crm, on the entrypoint crm binds. */
export interface OrderFunnelSource {
  listOrderFunnelFacts(caller: Caller, input: ListOrderFunnelFactsInput): Promise<Result<CursorPage<OrderFunnelFact>>>;
}
